import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Alert
} from '@mui/material';
import PersianDatePicker from './PersianDatePicker';
import { usersAPI, menuItemsAPI, reservationsAPI, User, MenuItem as MenuItemType, getImageUrl } from '../services/api';
import { toPersianNumber } from '../utils/persianNumber';

interface StaffReservationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const StaffReservationDialog: React.FC<StaffReservationDialogProps> = ({ open, onClose, onSuccess }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [mealType, setMealType] = useState<'breakfast' | 'lunch' | 'dinner'>('lunch');
  
  const [menuItems, setMenuItems] = useState<MenuItemType[]>([]);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [selectedMealId, setSelectedMealId] = useState<string>('');
  
  const [isCorporate, setIsCorporate] = useState<boolean>(true);
  
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch users when dialog opens
  useEffect(() => {
    if (open && users.length === 0) {
      fetchUsers();
    }
  }, [open]);

  // Fetch menu items when date or mealType changes (and dialog is open)
  useEffect(() => {
    if (open) {
      fetchMenuItems();
    }
  }, [selectedDate, mealType, open]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const data = await usersAPI.getUsers(); // Get all users, mostly staff
      setUsers(data);
    } catch (err) {
      console.error('Failed to fetch users', err);
      setError('خطا در دریافت لیست پرسنل');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchMenuItems = async () => {
    try {
      setLoadingMenu(true);
      const items = await menuItemsAPI.getMenuItems(selectedDate, selectedDate);
      // Filter by selected meal type and active status
      const filtered = items.filter((item: MenuItemType) => item.mealType === mealType && item.isActive);
      setMenuItems(filtered);
      setSelectedMealId(''); // Reset selected meal when menu changes
    } catch (err) {
      console.error('Failed to fetch menu items', err);
      setError('خطا در دریافت منوی غذا');
    } finally {
      setLoadingMenu(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedUser) {
      setError('لطفاً یک کاربر را انتخاب کنید');
      return;
    }
    if (!selectedMealId) {
      setError('لطفاً غذا را انتخاب کنید');
      return;
    }

    try {
      setLoadingSubmit(true);
      setError(null);
      await reservationsAPI.createReservation({
        userId: selectedUser.id,
        mealId: selectedMealId, // This should be the meal.id based on the backend API requirement
        mealDate: selectedDate,
        mealType: mealType,
        isCorporate: isCorporate
      });
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در ثبت رزرو');
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    setSelectedMealId('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>
        ثبت رزرو برای پرسنل
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ fontFamily: 'Vazir, sans-serif' }}>
              {error}
            </Alert>
          )}

          {/* User Selection */}
          <Autocomplete
            options={users}
            loading={loadingUsers}
            getOptionLabel={(option) => `${option.firstName || ''} ${option.lastName || ''} - ${toPersianNumber(option.personnelCode || '')}`}
            value={selectedUser}
            onChange={(_, newValue) => setSelectedUser(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="انتخاب پرسنل"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                  sx: { height: 48, borderRadius: '12px', fontFamily: 'Vazir, sans-serif' }
                }}
                InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ fontFamily: 'Vazir, sans-serif' }}>
                {option.firstName} {option.lastName} 
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontFamily: 'Vazir, sans-serif' }}>
                  ({toPersianNumber(option.personnelCode || 'بدون کد')})
                </Typography>
              </Box>
            )}
          />

          {/* Date Selection */}
          <Box sx={{ '& .MuiFormControl-root, & .MuiInputBase-root': { height: 48, borderRadius: '12px' } }}>
            <PersianDatePicker
              label="تاریخ سرو"
              value={selectedDate}
              onChange={setSelectedDate}
              minDate={new Date().toISOString().split('T')[0]}
            />
          </Box>

          {/* Meal Type Selection */}
          <TextField
            select
            label="وعده غذایی"
            value={mealType}
            onChange={(e) => setMealType(e.target.value as any)}
            InputProps={{ sx: { height: 48, borderRadius: '12px', fontFamily: 'Vazir, sans-serif' } }}
            InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
          >
            <MenuItem value="breakfast" sx={{ fontFamily: 'Vazir, sans-serif' }}>صبحانه</MenuItem>
            <MenuItem value="lunch" sx={{ fontFamily: 'Vazir, sans-serif' }}>ناهار</MenuItem>
            <MenuItem value="dinner" sx={{ fontFamily: 'Vazir, sans-serif' }}>شام</MenuItem>
          </TextField>

          {/* Meal Selection */}
          <TextField
            select
            label={loadingMenu ? "در حال بارگذاری منو..." : "انتخاب غذا"}
            value={selectedMealId}
            onChange={(e) => setSelectedMealId(e.target.value)}
            disabled={loadingMenu || menuItems.length === 0}
            InputProps={{ sx: { height: 48, borderRadius: '12px', fontFamily: 'Vazir, sans-serif' } }}
            InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
          >
            {menuItems.length === 0 && !loadingMenu ? (
              <MenuItem value="" disabled sx={{ fontFamily: 'Vazir, sans-serif' }}>
                غذایی برای این تاریخ ثبت نشده است
              </MenuItem>
            ) : (
              menuItems.map((item) => (
                <MenuItem key={item.id} value={item.mealId} sx={{ fontFamily: 'Vazir, sans-serif', display: 'flex', alignItems: 'center', gap: 1 }}>
                  {item.meal.imageUrl && (
                    <img 
                      src={getImageUrl(item.meal.imageUrl)} 
                      alt={item.meal.title} 
                      style={{ width: 24, height: 24, borderRadius: '4px', objectFit: 'cover' }} 
                    />
                  )}
                  {item.meal.title}
                  {item.isCorporate && (
                    <Typography variant="caption" sx={{ ml: 'auto', bgcolor: 'primary.light', color: 'white', px: 1, borderRadius: 1 }}>
                      سازمانی
                    </Typography>
                  )}
                </MenuItem>
              ))
            )}
          </TextField>

          {/* Is Corporate Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isCorporate}
                onChange={(e) => setIsCorporate(e.target.checked)}
                color="primary"
              />
            }
            label={<Typography sx={{ fontFamily: 'Vazir, sans-serif' }}>غذای سازمانی (استفاده از سهمیه)</Typography>}
          />

        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={handleClose} 
          disabled={loadingSubmit}
          sx={{ fontFamily: 'Vazir, sans-serif', borderRadius: '12px', height: 48, px: 3 }}
        >
          انصراف
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loadingSubmit || !selectedUser || !selectedMealId}
          sx={{ fontFamily: 'Vazir, sans-serif', borderRadius: '12px', height: 48, px: 4, fontWeight: 'bold' }}
        >
          {loadingSubmit ? <CircularProgress size={24} color="inherit" /> : 'ثبت رزرو'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaffReservationDialog;
